import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Extracts a user-friendly error message from a Supabase Edge Function invocation response.
 *
 * Edge functions can return errors in multiple ways:
 * 1. In `response.response?.json()` - the actual HTTP response body (MOST COMMON)
 * 2. In the `error` object returned by `.invoke()` (network errors, function not found, etc.)
 * 3. In the `data.error` field when the function returns `{ success: false, error: "..." }`
 * 4. In the `data.message` field for custom error messages
 *
 * This utility extracts the most relevant error message from any of these sources.
 *
 * @param response - The full response object from supabase.functions.invoke()
 * @param fallbackMessage - Optional fallback message if no error details are found
 * @returns A Promise that resolves to a user-friendly error message string
 *
 * @example
 * const response = await supabase.functions.invoke('my-function', { body: {...} });
 * const { data, error } = response;
 *
 * if (error || !data?.success) {
 *   const errorMessage = await getEdgeFunctionError(response, 'Operation failed');
 *   toast({ title: 'Error', description: errorMessage, variant: 'destructive' });
 * }
 */
export async function getEdgeFunctionError(
  response: any,
  fallbackMessage: string = 'An unexpected error occurred'
): Promise<string> {
  // Priority 1: Parse the actual HTTP response body (contains the real error message)
  try {
    if (response?.response) {
      const responseBody = await response.response.json();
      if (responseBody?.error) {
        return typeof responseBody.error === 'string'
          ? responseBody.error
          : JSON.stringify(responseBody.error);
      }
      if (responseBody?.message) {
        return typeof responseBody.message === 'string'
          ? responseBody.message
          : JSON.stringify(responseBody.message);
      }
    }
  } catch (jsonError) {
    // If JSON parsing fails, continue to other methods
    console.warn('Failed to parse edge function response:', jsonError);
  }

  const { data, error } = response || {};

  // Priority 2: Check if data has an error field (function returned error)
  if (data?.error) {
    return typeof data.error === 'string' ? data.error : JSON.stringify(data.error);
  }

  // Priority 3: Check if data has a message field (custom error message)
  if (data?.message && !data?.success) {
    return typeof data.message === 'string' ? data.message : JSON.stringify(data.message);
  }

  // Priority 4: Check the error object itself
  if (error) {
    // If error is a string
    if (typeof error === 'string') {
      return error;
    }

    // If error has a message property
    if (error.message) {
      return error.message;
    }

    // If error has a msg property (some edge functions use this)
    if (error.msg) {
      return error.msg;
    }

    // Try to stringify the error object
    try {
      const errorStr = JSON.stringify(error);
      if (errorStr && errorStr !== '{}') {
        return errorStr;
      }
    } catch {
      // Fall through to fallback
    }
  }

  // Priority 5: Use fallback message
  return fallbackMessage;
}
